/**
 * Admin API Hooks (Users, Roles, API Keys, Audit Logs)
 * For Media Devoted | Created By Ciro Autiero
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/services/api';

// Types
export interface Permission {
  id: string;
  code: string;
  name: string;
  description?: string;
  category: string;
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  status: 'active' | 'inactive' | 'suspended' | 'pending';
  role: Role;
  createdAt: string;
  lastLoginAt?: string;
}

export interface Role {
  id: string;
  name: string;
  displayName?: string;
  description?: string;
  permissions: Permission[];
  isSystem?: boolean;
}

export interface ApiKey {
  id: string;
  name: string;
  keyPreview: string;
  permissions: string[];
  expiresAt?: string;
  lastUsedAt?: string;
  isActive: boolean;
  createdAt: string;
  user?: {
    id: string;
    email: string;
  };
}

export interface AuditLog {
  id: string;
  userId: string;
  userEmail: string;
  userRole?: string;
  action: string;
  category: string;
  entityType?: string;
  entityId?: string;
  entityName?: string;
  requestBody?: Record<string, unknown>;
  changes?: { before?: Record<string, unknown>; after?: Record<string, unknown> };
  success: boolean;
  errorMessage?: string;
  durationMs?: number;
  ipAddress?: string;
  userAgent?: string;
  sessionId?: string;
  createdAt: string;
}

export interface SystemConfig {
  id: string;
  key: string;
  value: string;
  valueType: string;
  category: string;
  description?: string;
  isEditable: boolean;
}

// Query Keys
export const adminKeys = {
  users: () => ['users'] as const,
  user: (id: string) => ['users', id] as const,
  roles: () => ['roles'] as const,
  role: (id: string) => ['roles', id] as const,
  apiKeys: () => ['api-keys'] as const,
  auditLogs: (params?: Record<string, unknown>) => ['audit-logs', params] as const,
  recentActivity: (limit: number) => ['audit-recent', limit] as const,
  systemConfig: () => ['system-config'] as const,
};

// Users Hooks
export function useUsers() {
  return useQuery({
    queryKey: adminKeys.users(),
    queryFn: async () => {
      const response = await api.get<{ data: { users: User[]; pagination: { total: number } } }>('/users');
      return response.data.data.users;
    },
  });
}

export function useUser(id: string) {
  return useQuery({
    queryKey: adminKeys.user(id),
    queryFn: async () => {
      const response = await api.get<{ data: User }>(`/users/${id}`);
      return response.data.data;
    },
    enabled: !!id,
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      email: string;
      password: string;
      firstName: string;
      lastName: string;
      roleId?: string;
    }) => {
      const response = await api.post<{ data: User }>('/users', data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.users() });
    },
  });
}

export type UpdateUserPayload = Partial<Omit<User, 'role'>> & {
  roleId?: string;
  password?: string;
};

export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateUserPayload }) => {
      const response = await api.put<{ data: User }>(`/users/${id}`, data);
      return response.data.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: adminKeys.user(id) });
      queryClient.invalidateQueries({ queryKey: adminKeys.users() });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/users/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.users() });
    },
  });
}

// Roles Hooks
export function useRoles() {
  return useQuery({
    queryKey: adminKeys.roles(),
    queryFn: async () => {
      const response = await api.get<{ data: Role[] }>('/roles');
      return response.data.data;
    },
  });
}

export function useRolePermissions() {
  return useQuery({
    queryKey: ['roles', 'permissions'] as const,
    queryFn: async () => {
      const response = await api.get<{ data: Record<string, Permission[]> }>('/roles/permissions');
      return response.data.data;
    },
    staleTime: 5 * 60_000,
  });
}

export function useCreateRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { name: string; displayName: string; description?: string; permissionIds?: string[] }) => {
      const response = await api.post<{ data: Role }>('/roles', data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.roles() });
    },
  });
}

export function useUpdateRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: { displayName?: string; description?: string; permissionIds?: string[] };
    }) => {
      const response = await api.put<{ data: Role }>(`/roles/${id}`, data);
      return response.data.data;
    },
    onSuccess: (updatedRole) => {
      // Optimistically update cache so UI reflects changes immediately
      queryClient.setQueryData(adminKeys.roles(), (old: Role[] | undefined) => {
        if (!old) return old;
        return old.map((r) => (r.id === updatedRole.id ? updatedRole : r));
      });
      queryClient.invalidateQueries({ queryKey: adminKeys.roles() });
    },
  });
}

export function useDeleteRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/roles/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.roles() });
    },
  });
}

// API Keys Hooks
export function useApiKeys() {
  return useQuery({
    queryKey: adminKeys.apiKeys(),
    queryFn: async () => {
      const response = await api.get<{ data: ApiKey[] }>('/api-keys');
      return response.data.data;
    },
  });
}

export function useCreateApiKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      name: string;
      permissions?: string[];
      expiresAt?: string;
      userId?: string;
    }) => {
      // Use admin endpoint if userId is provided, otherwise my-keys
      const endpoint = data.userId ? '/api-keys/admin' : '/api-keys/my-keys';
      const response = await api.post<{ data: { apiKey: ApiKey; plainTextKey: string } }>(
        endpoint,
        data
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.apiKeys() });
    },
  });
}

export function useRevokeApiKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/api-keys/${id}/revoke`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.apiKeys() });
    },
  });
}

export function useDeleteApiKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api-keys/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.apiKeys() });
    },
  });
}

// Audit Logs Hooks
export function useAuditLogs(params?: {
  userId?: string;
  action?: string;
  category?: string;
  success?: boolean;
  limit?: number;
  page?: number;
}) {
  return useQuery({
    queryKey: adminKeys.auditLogs(params),
    queryFn: async () => {
      const response = await api.get<{ data: { logs: AuditLog[]; pagination: { total: number } } }>('/audit/logs', { params });
      return response.data.data.logs;
    },
  });
}

export function useAuditLog(id: string) {
  return useQuery({
    queryKey: ['audit-log', id] as const,
    queryFn: async () => {
      const response = await api.get<{ data: AuditLog }>(`/audit/logs/${id}`);
      return response.data.data;
    },
    enabled: !!id,
  });
}

export function useDeleteAuditLog() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/audit/logs/${id}`);
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['audit-logs'] });
      queryClient.invalidateQueries({ queryKey: ['audit-recent'] });
      queryClient.removeQueries({ queryKey: ['audit-log', id] });
    },
  });
}

export function useRecentActivity(limit = 5) {
  return useQuery({
    queryKey: adminKeys.recentActivity(limit),
    queryFn: async () => {
      const response = await api.get<{ data: AuditLog[] }>('/audit/recent', { params: { limit } });
      return response.data.data;
    },
    staleTime: 15_000,
  });
}

// System Config Hooks
export function useSystemConfig() {
  return useQuery({
    queryKey: adminKeys.systemConfig(),
    queryFn: async () => {
      const response = await api.get<{ data: SystemConfig[] }>('/system-config');
      return response.data.data;
    },
  });
}

export function useUpdateSystemConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ key, value }: { key: string; value: string | number | boolean }) => {
      const response = await api.put<{ data: SystemConfig }>(`/system-config/${key}`, { value });
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.systemConfig() });
    },
  });
}
